<script setup lang="ts">
import { computed } from 'vue'

import { useUiKitLink } from '@ui/composables/useUiKitLink'

import { overlayLayerTarget } from '@/utils/overlayLayers'
import { vSafeHtml } from '@ui/utils/safeHtml'

import AppButton from '@ui/app-button/AppButton.vue'
import AppIcon from '@ui/app-icon/AppIcon.vue'
import AppIconButton from '@ui/app-icon-button/AppIconButton.vue'

import { useNotificationStore } from './stores/useNotificationStore'
import type { Notification, NotificationType } from './types'

const layerTarget = overlayLayerTarget('notifications')
const notificationStore = useNotificationStore()

const { navigate } = useUiKitLink()

const topRightNotifications = computed(() => {
  return notificationStore.notifications.filter((item) => item.position === 'top-right')
})

const bottomCenterNotifications = computed(() => {
  return notificationStore.notifications.filter((item) => item.position === 'bottom-center')
})

const positions = computed(() => {
  return [
    { value: 'top-right', data: topRightNotifications.value },
    { value: 'bottom-center', data: bottomCenterNotifications.value },
  ]
})

const handleClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (!target.classList?.contains('notification-anchor')) return
  const url = target.getAttribute('data-to')
  navigate(url as string)
}

const getIcon = (item: Notification) => {
  return ({
    success: 'CheckSolid',
    info: 'InfoSolid',
    error: 'ErrorSolid',
    loading: 'Spinner',
  } satisfies Record<NotificationType, string>)[item.type]
}

const truncateText = (textValue = '') => {
  const maximumTextLength = 309
  return textValue.length > maximumTextLength ? `${textValue.slice(0, maximumTextLength)}...` : textValue
}

const handleMouseEnter = (id: number) => {
  notificationStore.clearTimer(id)
}

const handleMouseLeave = (id: number) => {
  notificationStore.startTimer(id)
}

const closeItem = (id: number) => {
  notificationStore.removeItem(id)
}

const handleActionClick = (item: Notification) => {
  item.action?.onClick?.()
  closeItem(item.id)
}
</script>

<template>
  <teleport :to="layerTarget">
    <div class="notification-wrapper" data-test-id="app-notification-wrapper">
      <transition-group
        v-for="position in positions"
        :key="position.value"
        name="slide-to-top"
        tag="div"
        class="notification-list"
        :class="position.value"
        aria-live="polite"
        aria-atomic="false"
      >
        <div
          v-for="item in position.data"
          :key="item.id"
          class="notification-item font-medium-body"
          :class="`type-${item.type}`"
          :role="item.type === 'error' ? 'alert' : 'status'"
          data-test-id="app-notification-item"
          @mouseenter="handleMouseEnter(item.id)"
          @mouseleave="handleMouseLeave(item.id)"
        >
          <app-icon
            :name="getIcon(item)"
            :size="item.type === 'loading' ? '20px' : '24px'"
            :class="{
              'loading-spinner': item.type === 'loading',
            }"
          />

          <div
            v-safe-html="truncateText(item.text)"
            data-test-id="app-notification-text"
            @click="handleClick"
          />

          <app-button
            v-if="item.action"
            class="notification-action"
            size="sm"
            variant="tertiary"
            data-test-id="app-notification-action"
            @click="handleActionClick(item)"
          >
            {{ item.action.label }}
          </app-button>

          <app-icon-button
            class="notification-close-button"
            size="sm"
            variant="no-fill"
            icon="Close"
            aria-label="Close notification"
            data-test-id="app-notification-close"
            @click="closeItem(item.id)"
          />
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<style lang="scss" scoped>
:deep(.notification-anchor) {
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
}

.notification {
  &-wrapper {
    position: fixed;
    top: 0;
    left: 0;
  }

  &-list {
    position: fixed;
    display: inline-flex;
    flex-direction: column;
    min-width: 278px;
    max-width: 526px;
    gap: 12px;

    &.top-right {
      top: 10px;
      right: 10px;

      @media (max-width: 767px) {
        inset: auto 10px 90px;
        min-width: 0;
        max-width: none;
        align-items: center;
      }
    }

    &.bottom-center {
      bottom: 72px;
      left: 50%;
      transform: translateX(-50%);

      @media (max-width: 767px) {
        right: 10px;
        bottom: 90px;
        left: 10px;
        transform: none;
        min-width: 0;
        max-width: none;
      }
    }
  }

  &-item {
    display: flex;
    align-items: center;
    padding: 18px 20px;
    gap: 16px;
    border-radius: 12px;
    background: var(--g-neutral-1);
    box-shadow: 0 12px 36px 0 rgb(20 21 64 / 24%);
    color: var(--g-neutral-8);
  }

  &-close-button {
    margin-left: auto;
    color: var(--g-neutral-8);
  }

  &-action {
    margin-left: auto;
    color: var(--g-neutral-8);

    & + .notification-close-button {
      margin-left: 0;
    }
  }
}
</style>
